import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { presetRange, reportTimeZone } from '@/lib/dateRange';
import { useBranchStore, useFeatureFlagsStore } from '@/stores';

/**
 * iter880 — Shared DateRangeFilter used across POS Orders, Banking Hub,
 * Stock In Log, Cash Transfers, Dashboard, etc. Emits { start_date, end_date }
 * (YYYY-MM-DD strings). Provides quick presets so users don't have to
 * hand-type dates for common cases.
 *
 * Props:
 *   value: { start_date, end_date }
 *   onChange: fn({ start_date, end_date })
 *   className: string
 *   testid: string (prefix for data-testids; defaults to 'date-range-filter')
 */
export function DateRangeFilter({ value = {}, onChange, className = '', testid = 'date-range-filter', timeZone, branchIds }) {
  // R3 — shared local/branch-timezone presets (was UTC → wrong "Today" after local midnight).
  const branches = useBranchStore((st) => st.branches);
  const companyTz = useFeatureFlagsStore((st) => st.companyTz);
  // C3 priority: shared branch tz → company tz → device tz.
  const tz = timeZone || reportTimeZone(branches || [], branchIds) || companyTz;
  const preset = (key) => {
    const r = presetRange(key, tz);
    return { start_date: r.start, end_date: r.end };
  };
  const label = value.start_date && value.end_date
    ? (value.start_date === value.end_date ? value.start_date : `${value.start_date} → ${value.end_date}`)
    : 'All time';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`gap-2 h-10 ${className}`} data-testid={`${testid}-trigger`}>
          <CalendarIcon size={14} />
          <span className="text-xs">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-3" align="start">
        <div className="grid grid-cols-3 gap-1.5">
          {[
            ['Today', preset('today')],
            ['Yesterday', preset('yesterday')],
            ['This week', preset('week')],
            ['7 days', preset('last7')],
            ['This month', preset('month')],
            ['30 days', preset('last30')],
            ['90 days', preset('last90')],
            ['This year', preset('year')],
          ].map(([label, range]) => (
            <Button key={label} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onChange(range)} data-testid={`${testid}-preset-${label.toLowerCase().replace(/\s+/g, '-')}`}>{label}</Button>
          ))}
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] text-muted-foreground">From</label>
          <Input type="date" value={value.start_date || ''} onChange={(e) => onChange({ ...value, start_date: e.target.value })} className="h-8 text-xs" data-testid={`${testid}-start`} />
          <label className="text-[10px] text-muted-foreground">To</label>
          <Input type="date" value={value.end_date || ''} onChange={(e) => onChange({ ...value, end_date: e.target.value })} className="h-8 text-xs" data-testid={`${testid}-end`} />
        </div>
        {(value.start_date || value.end_date) && (
          <Button size="sm" variant="ghost" className="w-full h-7 text-xs text-rose-600 hover:text-rose-700 gap-1" onClick={() => onChange({ start_date: null, end_date: null })} data-testid={`${testid}-clear`}>
            <X size={12} /> Clear
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default DateRangeFilter;
